import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';

import { Formik } from 'formik';

import { ZariContext } from '_zari/context';
import { Divider, Form, Button, Row, Col, Modal } from '_zari/core';
import Yup from '_zari/validation/_yup';

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones';
import TiposDeDocumentoSunatConstant from 'constants/tiposDeDocumentoSunat';

import { DateHandler, IdGen, RounderUtil } from 'utils';

import * as InventarioCrud from 'services/inventario.service';
import * as RequerimientoService from 'services/requerimiento.service';

import { RetrocederButton } from 'sharedComponents/buttons';

import * as FormGroups from './formGroups';

import DetallesSection from './sections/detalles';
import RequerimientosEnAlmacenModal from './modals/requerimientosEnAlmacen';
import { ENTRADA } from 'constants/seInventarios';
import { SALIDA } from 'constants/tiposTransferencia';
import { RETIRO_PARA_ENTREGA_A_TRABAJADORES } from 'constants/tiposOperacionSunat';

const validationSchema = Yup.object().shape({
  fechaEmision: Yup.string().required(),
  numeroDocumento: Yup.string().required(),
  // La serie solo es obligatoria cuando NO es Otros/Ticket (00).
  // Para Guía de Remisión (09) y similares, sí es requerida.
  serieDocumento: Yup.string().when('tipoDocumento', {
    is: (val) => val && val !== '00',
    then: Yup.string().required(),
    otherwise: Yup.string()
  }),
  tipoDocumento: Yup.string().required(),
  tipoOperacion: Yup.string().required(),
  moneda: Yup.string().required(),
  titulo: Yup.string().required()
});

const actualizarFila = (current, { cantidad, precio, ...restPayload }) => {
  current = { ...current, ...restPayload, cantidad, precio };
  current.subTotal = RounderUtil.round(current.cantidad * current.precio);
  return current;
};

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      let newState = state
        .map(val => ({
          ...val,
          __filaActiva: false,
          __focusCantidad: false,
          __focusDescuento: false,
          __focusPrecio: false,
          esFilaBusqueda: false
        }))
        .filter(x => x._idProductoPresentacion);

      let { viaCodBar, _id, ...payload } = action.payload;
      return [
        ...newState,
        {
          ...payload,
          __filaActiva: !viaCodBar,
          __focusCantidad: !viaCodBar,
          _idProductoPresentacion: _id,
          _idFila: IdGen.newId(),
          cantidad: '1'
        },
        {
          __filaActiva: viaCodBar,
          esFilaBusqueda: true,
          _idFila: IdGen.newId()
        }
      ];
    }
    case 'actualizar': {
      const { saltarFila, ...nuevoPayload } = action.payload;
      return state.map(val => {
        if (val.esFilaBusqueda) {
          val.__filaActiva = saltarFila;
        } else {
          if (val._idFila === nuevoPayload._idFila) {
            val = actualizarFila(val, nuevoPayload);
          }
          val.__filaActiva = !saltarFila;
        }
        return val;
      });
    }
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila);
    case 'fill-requerimientos': {
      // Limpiar filas existentes y agregar productos de requerimientos
      const nuevasFilas = action.payload.map(item => ({
        ...item,
        _idProductoPresentacion: item._id,
        _idFila: IdGen.newId(),
        __filaActiva: false,
        __focusCantidad: false,
        __focusDescuento: false,
        __focusPrecio: false,
        precioNoEditable: true
      }));
      return [
        ...nuevasFilas,
        { esFilaBusqueda: true, _idFila: IdGen.newId() }
      ];
    }
    default:
      return state;
  }
};

const MainForm = ({ edicionInfo, idRegistro: idRegistroProp }) => { // ✅ MOD: recibe idRegistro del padre

  const __zariContext = React.useContext(ZariContext);
  const location = useLocation();
  const history = useHistory();

  const parsedParams = React.useMemo(() => queryString.parse(location.search), [location]);

  // MODO EDICIÓN: El ID ya viene cargado en edicionInfo desde el padre
  const idRegistro = idRegistroProp || edicionInfo?._id || edicionInfo?.id || null; // ✅ MOD: no usar props (evita no-undef)

  const [archivosSeleccionados, setArchivosSeleccionados] = React.useState([]);
  const [adjuntosExistentes, setAdjuntosExistentes] = React.useState([]);
  const [adjuntosExistentesBackup, setAdjuntosExistentesBackup] = React.useState([]); // ✅ ADD
  const [flagLimpiarAdjuntos, setFlagLimpiarAdjuntos] = React.useState(false); // ✅ ADD

  // Requerimientos en almacén (salida tipo 36)
  const [mostrarRequerimientosModal, setMostrarRequerimientosModal] = React.useState(false);
  const [idsRequerimientosSeleccionados, setIdsRequerimientosSeleccionados] = React.useState([]);
  const [cantidadRequerimientosDisponibles, setCantidadRequerimientosDisponibles] = React.useState(0);

  // Hoja de Entrega modal
  const [mostrarHojaEntregaModal, setMostrarHojaEntregaModal] = React.useState(false);
  const [hojaEntregaBlobUrl, setHojaEntregaBlobUrl] = React.useState('');

  // Pre-cargar conteo de requerimientos disponibles
  React.useEffect(() => {
    if (parsedParams.to !== RETIRO_PARA_ENTREGA_A_TRABAJADORES) return;
    RequerimientoService.obtenerEnAlmacenParaSalida()
      .then(({ data }) => setCantidadRequerimientosDisponibles(Array.isArray(data) ? data.length : 0))
      .catch(() => {})
  }, [parsedParams.to]);

  // Carga de adjuntos si es edición
  React.useEffect(() => {
    if (!idRegistro) return; // ✅ MOD: si no hay id, no hay adjuntos que listar

    console.log('[ADJUNTOS] idRegistro =>', idRegistro); // ✅ MOD: traza para confirmar que entra en edición

    InventarioCrud.listarAdjuntos(idRegistro)
      .then(res => {
        console.log('[ADJUNTOS] response =>', res?.data); // ✅ MOD: traza de respuesta real
        const payload = res?.data?.data ?? res?.data?.adjuntos ?? res?.data ?? []; // ✅ MOD: soporta distintas estructuras
        const lista = Array.isArray(payload) ? payload : []; // ✅ MOD: asegura array
        setAdjuntosExistentes(lista); // ✅ MOD
        setAdjuntosExistentesBackup(lista); // ✅ ADD
        setFlagLimpiarAdjuntos(false);      // ✅ ADD (al cargar, no está “limpiado”)
        if (lista.length === 0) __zariContext.snack('info', 'Este registro no tiene adjuntos'); // ✅ MOD: feedback visible
      })
      .catch(err => {
        console.error("Error cargando adjuntos", err); // ✅ MOD
        __zariContext.snack('error', 'No se pudo cargar adjuntos (revisa Network/Console)'); // ✅ MOD
      });
  }, [idRegistro, __zariContext]); // ✅ MOD: agrega __zariContext como dependencia


  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setArchivosSeleccionados(files);
  };

  const handleDescargar = async (adjunto) => {
    try {
      const response = await InventarioCrud.descargarAdjunto(adjunto._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', adjunto.nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      __zariContext.snack('error', 'No se pudo descargar el archivo');
    }
  };

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, edicionInfo.detalles, inputDets => {
    if (inputDets) {
      inputDets = inputDets.map(x => ({
        ...x,
        __filaActiva: false,
        __focusCantidad: false,
        __focusDescuento: false,
        __focusPrecio: false,
        _idFila: IdGen.newId()
      }));
      return [...inputDets, { esFilaBusqueda: true, _idFila: IdGen.newId() }];
    }
    return [{ esFilaBusqueda: true, _idFila: IdGen.newId() }];
  });

  const formInitialValues = React.useMemo(() => {
    const nuevaFecha = DateHandler.format(new Date());
    const parsed = queryString.parse(location.search);
    return {
      _idTaller: edicionInfo._idTaller || parsed.taller || '',
      fechaEmision: edicionInfo.fechaEmision || nuevaFecha,
      numeroDocumento: edicionInfo.numeroDocumento || '',
      serieDocumento: edicionInfo.serieDocumento || '',
      moneda: edicionInfo.moneda || 'SOLES',
      tipoDocumento: edicionInfo.tipoDocumento || TiposDeDocumentoSunatConstant.OTROS,
      tipoOperacion: edicionInfo.tipoOperacion || parsed.to,
      titulo: edicionInfo.titulo || '',
      _idOficinaSolicitante: edicionInfo._idOficinaSolicitante ?? '',
      _idUsuarioSolicitante: edicionInfo._idUsuarioSolicitante ?? '',
      _idUsuarioRecibe: edicionInfo._idUsuarioRecibe ?? ''
    };
  }, [edicionInfo, location.search]);

  const submitHandler = React.useCallback(
    (data, { setSubmitting }) => {
      const esSalidaActual = parsedParams.t === SALIDA;
      const detallesNormalizados = detalles
        .filter(x => x._idProductoPresentacion)
        .filter(x => {
          const cant = parseFloat(x.cantidad);
          return cant > 0;
        })
        .map(x => ({
          _idProductoPresentacion: x._idProductoPresentacion,
          _idCentroCostoNivel1: x._idCentroCostoNivel1,
          _idCentroCostoNivel2: x._idCentroCostoNivel2,
          cantidad: parseFloat(x.cantidad),
          precio: esSalidaActual ? 0 : parseFloat(x.precio || 0)
        }));

      if (detallesNormalizados.length === 0) {
        __zariContext.snack('error', 'Ingresar detalles.');
        return;
      }

      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });
      formData.append('detalles', JSON.stringify(detallesNormalizados));

      // Requerimientos seleccionados (salida tipo 36)
      if (idsRequerimientosSeleccionados.length > 0) {
        formData.append('_idsRequerimientos', JSON.stringify(idsRequerimientosSeleccionados));
      }

      formData.append('flagLimpiarAdjuntos', flagLimpiarAdjuntos ? '1' : '0'); // ✅ ADD

      archivosSeleccionados.forEach((archivo) => {
        formData.append('adjuntos', archivo);
      });

      const promiseCrearEditar = idRegistro
        ? () => InventarioCrud.actualizar(idRegistro, formData)
        : () => InventarioCrud.nuevo(formData);

      const stringParams = queryString.stringify(parsedParams);

      promiseCrearEditar()
        .then(({ data }) => {
          __zariContext.snack('success', 'El registro se ha guardado.');

          // Si es salida tipo 36 y se creó (no edición), mostrar Hoja de Entrega
          if (esSalidaActual && parsedParams.to === RETIRO_PARA_ENTREGA_A_TRABAJADORES && data && data._id) {
            InventarioCrud.descargarHojaEntregaxId(data._id)
              .then(({ data: pdfData }) => {
                const url = URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }));
                setHojaEntregaBlobUrl(url);
                setMostrarHojaEntregaModal(true);
              })
              .catch(() => {
                // Si falla el PDF, redirigir igual
                const paramsKeys = Object.keys(parsedParams);
                const flagTaller = paramsKeys.some(x => x.startsWith('cc'));
                history.push(`/${flagTaller ? CodigosTransaccionesConstant.TALLERES_LISTA : CodigosTransaccionesConstant.INVENTARIOS_ENTRADASALIDA_LISTA}?${stringParams}`);
              });
          } else {
            const paramsKeys = Object.keys(parsedParams);
            const flagTaller = paramsKeys.some(x => x.startsWith('cc'));
            history.push(`/${flagTaller ? CodigosTransaccionesConstant.TALLERES_LISTA : CodigosTransaccionesConstant.INVENTARIOS_ENTRADASALIDA_LISTA}?${stringParams}`);
          }
        })
        .catch((err) => {
          __zariContext.snack('error', err.data?.error || 'Error al guardar');
          setSubmitting(false);
        });
    },
    [detalles, __zariContext, history, idRegistro, parsedParams, archivosSeleccionados, idsRequerimientosSeleccionados]
  );

  // ===============================
  // ZIP (STORE) sin dependencias
  // ===============================
  const _zipCrcTable = (() => {
    let c;
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      c = n;
      for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
      table[n] = c >>> 0;
    }
    return table;
  })();

  const crc32 = (u8) => {
    let crc = 0 ^ -1;
    for (let i = 0; i < u8.length; i++) crc = (crc >>> 8) ^ _zipCrcTable[(crc ^ u8[i]) & 0xFF];
    return (crc ^ -1) >>> 0;
  };

  const u16 = (v) => new Uint8Array([v & 0xff, (v >>> 8) & 0xff]);
  const u32 = (v) => new Uint8Array([v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff]);

  const concatU8 = (chunks) => {
    const total = chunks.reduce((a, c) => a + c.length, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const c of chunks) { out.set(c, off); off += c.length; }
    return out;
  };

  // DOS date/time para ZIP
  const dosDateTime = (date = new Date()) => {
    const year = Math.max(1980, date.getFullYear());
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = Math.floor(date.getSeconds() / 2);

    const dosTime = (hours << 11) | (minutes << 5) | seconds;
    const dosDate = ((year - 1980) << 9) | (month << 5) | day;
    return { dosTime, dosDate };
  };

  const sanitizeName = (name) =>
    (name || 'archivo')
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
      .replace(/\s+/g, ' ')
      .trim();

  const buildZipStore = (files) => {
    const encoder = new TextEncoder();
    const localParts = [];
    const centralParts = [];
    let offset = 0;

    for (const f of files) {
      const filename = sanitizeName(f.name);
      const nameBytes = encoder.encode(filename);
      const dataU8 = f.data instanceof Uint8Array ? f.data : new Uint8Array(f.data);
      const { dosTime, dosDate } = dosDateTime(f.date || new Date());
      const c = crc32(dataU8);
      const size = dataU8.length;

      // Local file header
      const localHeader = concatU8([
        u32(0x04034b50),     // Local file header signature
        u16(20),             // Version needed to extract
        u16(0),              // General purpose bit flag
        u16(0),              // Compression method = 0 (STORE)
        u16(dosTime),
        u16(dosDate),
        u32(c),              // CRC-32
        u32(size),           // Compressed size
        u32(size),           // Uncompressed size
        u16(nameBytes.length),
        u16(0)               // Extra field length
      ]);

      localParts.push(localHeader, nameBytes, dataU8);

      // Central directory header
      const centralHeader = concatU8([
        u32(0x02014b50),     // Central file header signature
        u16(20),             // Version made by
        u16(20),             // Version needed to extract
        u16(0),              // General purpose bit flag
        u16(0),              // Compression method = 0 (STORE)
        u16(dosTime),
        u16(dosDate),
        u32(c),
        u32(size),
        u32(size),
        u16(nameBytes.length),
        u16(0),              // Extra length
        u16(0),              // File comment length
        u16(0),              // Disk number start
        u16(0),              // Internal file attributes
        u32(0),              // External file attributes
        u32(offset)          // Relative offset of local header
      ]);

      centralParts.push(centralHeader, nameBytes);

      offset += localHeader.length + nameBytes.length + dataU8.length;
    }

    const centralDir = concatU8(centralParts);
    const centralOffset = offset;
    offset += centralDir.length;

    // End of central directory record
    const eocd = concatU8([
      u32(0x06054b50),
      u16(0),                         // Number of this disk
      u16(0),                         // Disk where central directory starts
      u16(files.length),              // Number of central directory records on this disk
      u16(files.length),              // Total number of central directory records
      u32(centralDir.length),         // Size of central directory
      u32(centralOffset),             // Offset of start of central directory
      u16(0)                          // ZIP comment length
    ]);

    const zipU8 = concatU8([...localParts, centralDir, eocd]);
    return new Blob([zipU8], { type: 'application/zip' });
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDescargarZipAdjuntos = async () => {
    try {
      if (!adjuntosExistentes?.length) return;

      // Si solo hay 1, usa tu lógica actual (opcional)
      if (adjuntosExistentes.length === 1) {
        return handleDescargar(adjuntosExistentes[0]);
      }

      // 1) Bajar todos los adjuntos como arraybuffer (uno por uno)
      const files = [];
      for (const a of adjuntosExistentes) {
        const resp = await InventarioCrud.descargarAdjunto(a._id, { responseType: 'arraybuffer' });
        const u8 = new Uint8Array(resp.data);
        files.push({ name: a.nombre || `adjunto-${a._id}`, data: u8, date: new Date() });
      }

      // 2) Armar ZIP sin dependencias
      const zipBlob = buildZipStore(files);

      // 3) Descargar
      downloadBlob(zipBlob, `Documentos_Adjuntos.zip`);
    } catch (err) {
      console.error(err);
      __zariContext.snack('error', 'No se pudo generar/descargar el ZIP');
    }
  };

  return (
    <Row>
      <Col md={12}>
        <Formik initialValues={formInitialValues} onSubmit={submitHandler} validationSchema={validationSchema}>
          {({ errors, handleSubmit, isSubmitting, touched, values, handleBlur, setFieldValue }) => (
            <>
    <RequerimientosEnAlmacenModal
      show={mostrarRequerimientosModal}
      handleClose={() => setMostrarRequerimientosModal(false)}
      onSeleccionar={(ids, productosDetalles, infoRequerimiento) => {
        setIdsRequerimientosSeleccionados(ids);
        detallesDispatcher({ type: 'fill-requerimientos', payload: productosDetalles });
        if (infoRequerimiento) {
          setFieldValue('_idOficinaSolicitante', infoRequerimiento._idOficinaSolicitante);
          setFieldValue('_idUsuarioSolicitante', infoRequerimiento._idUsuarioSolicitante);
        }
      }}
    />
            <Form>
              <Row>
                <Col md={4}>
                  <FormGroups.SalidaEntradaTipoFormGroup value={parsedParams.t} />
                  <FormGroups.FechaEmisionFormGroup fIsSubmitting={isSubmitting} iError={errors.fechaEmision} iTouched={touched.fechaEmision} />
                  <FormGroups.TituloFormGroup fIsSubmitting={isSubmitting} iError={errors.titulo} iTouched={touched.titulo} />

                  {/* SECCIÓN ADJUNTOS */}
                  <div className="form-group row mt-3">
                    <label className="col-md-4 col-form-label text-right" style={{ fontWeight: 'normal', textTransform: 'uppercase', fontSize: '0.8rem' }}>
                      Adjuntar documentos
                    </label>

                    <div className="col-md-8">
                      <div className="d-flex align-items-center flex-wrap">
                        {(archivosSeleccionados.length > 0 || adjuntosExistentes.length > 0 || flagLimpiarAdjuntos) ? ( // ✅ MOD
                          <>
                            {/* ✅ MOD: 1) Seleccionar archivo */}
                            <div className="mb-2 mr-2">
                              <Button
                                as="label"
                                htmlFor="file-upload"
                                style={{ backgroundColor: '#ebedf2', color: '#3f4047', border: 'none', padding: '0.4rem 1rem', fontSize: '0.85rem' }}
                                className="mb-0 cursor-pointer"
                              >
                                Seleccionar archivo
                              </Button>
                              <input id="file-upload" type="file" multiple hidden onChange={handleFileChange} />
                            </div>

                            {/* ✅ MOD: 2) Limpiar */}
                            <Button
                              type="button"
                              variant={flagLimpiarAdjuntos ? "warning" : "primary"}
                              onClick={() => {
                                if (!flagLimpiarAdjuntos) {
                                  // ✅ LIMPIAR: desenlista todo y marca para borrar en BD al guardar
                                  setArchivosSeleccionados([]);     // ✅ ADD (por subir)
                                  setAdjuntosExistentes([]);        // existentes
                                  setFlagLimpiarAdjuntos(true);     // marca eliminación en BD al guardar
                                } else {
                                  // ✅ RECUPERAR: revierte el limpiar y cancela borrado en BD
                                  setAdjuntosExistentes(adjuntosExistentesBackup); // restaura existentes
                                  setFlagLimpiarAdjuntos(false);                   // cancela eliminación
                                }
                              }}
                              className="mb-2 mr-2"
                              style={{ padding: '0.4rem 1rem', fontSize: '0.85rem' }}
                            >
                              {flagLimpiarAdjuntos ? "Recuperar" : "Limpiar"}
                            </Button>

                            {/* ✅ MOD: 3) Descargar (verde) */}
                            {adjuntosExistentes.length > 0 && ( // ✅ MOD
                              <Button
                                type="button"
                                variant="success"
                                onClick={handleDescargarZipAdjuntos}
                                className="mb-2 mr-2"
                                style={{
                                  padding: '0.4rem 1rem',
                                  fontSize: '0.85rem',
                                  backgroundColor: '#28a745', // ✅ verde
                                  borderColor: '#28a745',     // ✅ borde verde
                                  color: '#fff'               // ✅ texto blanco
                                }}
                              >
                                Descargar
                              </Button>
                            )}
                          </>
                        ) : (
                          <div className="mb-2 mr-2">
                            <Button
                              as="label"
                              htmlFor="file-upload"
                              style={{ backgroundColor: '#ebedf2', color: '#3f4047', border: 'none', padding: '0.4rem 1rem', fontSize: '0.85rem' }}
                              className="mb-0 cursor-pointer"
                            >
                              Seleccionar archivo
                            </Button>
                            <input id="file-upload" type="file" multiple hidden onChange={handleFileChange} />
                          </div>
                        )}
                      </div>
                      {/* ARCHIVOS EXISTENTES (MISMO ESTILO QUE "POR SUBIR") */} {/* ✅ MOD */}
                      {adjuntosExistentes.map((adj, i) => ( // ✅ MOD
                        <div key={`ex-${i}`} className="text-primary small mb-1" style={{ fontSize: '0.75rem' }}>
                          <i className="fa fa-paperclip mr-1"></i> {adj.nombre} <span className="text-muted">(adjunto)</span>
                        </div>
                      ))}


                      {/* ARCHIVOS NUEVOS */}
                      {archivosSeleccionados.map((f, i) => (
                        <div key={i} className="text-primary small mb-1" style={{ fontSize: '0.75rem' }}>
                          <i className="fa fa-paperclip mr-1"></i> {f.name} <span className="text-muted">(por subir)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <FormGroups.TipoOperacionFormGroup value={parsedParams.to} />
                  <FormGroups.TipoDocumentoFormGroup fIsSubmitting={isSubmitting} iError={errors.tipoDocumento} iTouched={touched.tipoDocumento} />
                  {/* Serie solo aplica cuando NO es Otros/Ticket (00). En Guía de Remisión (09) y similares sí es obligatoria. */}
                  {values.tipoDocumento !== '00' && (
                    <FormGroups.SerieNumeroFormGroup fIsSubmitting={isSubmitting} handleBlur={handleBlur} setFieldValue={setFieldValue} vSerie={values.serieDocumento} vTipoDocumento={values.tipoDocumento} vTipoOperacion={values.tipoOperacion} iTouched={touched.serieDocumento} iError={errors.serieDocumento} />
                  )}
                </Col>

                <Col md={4}>
                  <FormGroups.GenerarNumeroFormGroup fIsSubmitting={isSubmitting} handleBlur={handleBlur} setFieldValue={setFieldValue} vSerie={values.serieDocumento} vTipoDocumento={values.tipoDocumento} vTipoOperacion={values.tipoOperacion} iTouched={touched.serieDocumento} iError={errors.serieDocumento} />
                  {parsedParams.t === SALIDA && (
                    <>
                      <FormGroups.OficinaFormGroup iError={errors._idOficinaSolicitante} iTouched={touched._idOficinaSolicitante} fIsSubmitting={isSubmitting} setFieldValue={setFieldValue} />
                      <FormGroups.UsuarioSolicitanteFormGroup iError={errors._idUsuarioSolicitante} iTouched={touched._idUsuarioSolicitante} fIsSubmitting={isSubmitting} vIdOficinaSolicitante={values._idOficinaSolicitante} vIdUsuarioSolicitante={values._idUsuarioSolicitante} />
                    </>
                  )}
                  {parsedParams.to === RETIRO_PARA_ENTREGA_A_TRABAJADORES && (
                    <FormGroups.UsuarioRecibeFormGroup fIsSubmitting={isSubmitting} vIdOficinaSolicitante={values._idOficinaSolicitante} />
                  )}
                  {parsedParams.to === RETIRO_PARA_ENTREGA_A_TRABAJADORES && (
                    <div className='form-group row'>
                      <div className='col-md-12 text-right'>
                        <Button
                          type='button'
                          variant='info'
                          onClick={() => setMostrarRequerimientosModal(true)}
                          style={{ fontSize: '0.85rem' }}
                        >
                          Ver Requerimientos ({idsRequerimientosSeleccionados.length > 0 ? idsRequerimientosSeleccionados.length : cantidadRequerimientosDisponibles})
                        </Button>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
              <Divider />
              <DetallesSection detalles={detalles} dispatch={detallesDispatcher} flagTaller={!!values._idTaller} tipoOperacion={values.tipoOperacion} esSalida={parsedParams.t === SALIDA} />
              <Divider />
              <Row>
                <Col md={12} className='text-right'>
                  <RetrocederButton />
                  <Button disabled={isSubmitting} onClick={handleSubmit} className='ml-2' variant="primary">
                    Guardar
                  </Button>
                </Col>
              </Row>
            </Form>
            </>
          )}
        </Formik>
      </Col>
      {mostrarHojaEntregaModal && hojaEntregaBlobUrl && (
        <Modal show={mostrarHojaEntregaModal} size='lg'>
          <Modal.Header>
            <Modal.Title>Hoja de Entrega</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <iframe
              src={hojaEntregaBlobUrl}
              style={{ width: '100%', height: '500px', border: 'none' }}
              title='Hoja de Entrega'
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant='secondary'
              onClick={() => {
                setMostrarHojaEntregaModal(false);
                if (hojaEntregaBlobUrl) URL.revokeObjectURL(hojaEntregaBlobUrl);
                const stringParams = queryString.stringify(parsedParams);
                const paramsKeys = Object.keys(parsedParams);
                const flagTaller = paramsKeys.some(x => x.startsWith('cc'));
                history.push(`/${flagTaller ? CodigosTransaccionesConstant.TALLERES_LISTA : CodigosTransaccionesConstant.INVENTARIOS_ENTRADASALIDA_LISTA}?${stringParams}`);
              }}
            >
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Row>
  );
};

export default MainForm;